import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ValidateDateRange} from '~shared/directives/form-validators/date-range-valid.directive';
import {DeliveryMethodsType} from '~core/enum/delivery-methods.enum';
import {Lottery} from '~core/models';
import {ProcessConfiguration} from '~core/models/tixx-select/process-configuration';
import {ProcessConfigurationService} from '~core/services/tixx-select/process-configuration.service';
import {TixxSelectTranslationsService} from '~shared/translations/alerts/tixx-select-overview-translations.service';
import {NotificationType} from '~core/enum/notification.enum';
import {DefaultNotificationService} from '~core/services';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Workspace sidebar for editing customer data
 */
@UntilDestroy()
@Component({
  selector: 'app-process-configuration',
  templateUrl: './process-configuration.component.html',
})
export class ProcessConfigurationComponent implements OnInit, OnDestroy {
  @Input() selectedLottery: Lottery;

  public processConfiguration: ProcessConfiguration;
  public updateProcessConfigurationForm: UntypedFormGroup;
  public spinnerName: string = 'processConfigurationSubmit';
  public yesOrNoDropdown: Array<any> = [];
  public deliveryDropdown: Array<any> = [];
  public possiblePriceDeviationDropdown: Array<any> = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private processConfigurationService: ProcessConfigurationService,
    public tixxSelectOverviewService: EventAdministrationService,
    public tixxSelectTranslationService: TixxSelectTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
  ) {
    this.processConfigurationService.castCurrentProcessConfiguration.pipe(untilDestroyed(this))
      .subscribe((data: ProcessConfiguration) => this.processConfiguration = data);
    this.yesOrNoDropdown.push(
      {value: true, status: this.flTranslations.i18nActivated},
      {value: false, status: this.flTranslations.i18nDeactivated},
    );
    this.possiblePriceDeviationDropdown.push(
      {value: 'deactivated', status: this.flTranslations.i18nDeactivated},
      {value: 'higher', status: this.flTranslations.i18nHigher},
      {value: 'lower', status: this.flTranslations.i18nLower},
      {value: 'both', status: this.flTranslations.i18nBoth}
    );

    this.deliveryDropdown = this.processConfiguration.delivery.deliveryMethods;
  }

  public ngOnInit(): void {
    this.fillForm();
  }

  public ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditProcessConfigurationModal(false);
  }

  /**
   * Create the form.
   */
  public fillForm(): void {
    const data = this.processConfiguration;
    // const delivery = data.availableDeliveryMethods;
    const timing = data.submissionCollectionTiming;

    this.updateProcessConfigurationForm = this.formBuilder.group({
      id: [data.id],
      optionalNonContiguousSeating: [data.optionalNonContiguousSeating],
      secondaryTicketRequestAvailable: [data.secondaryTicketRequestAvailable],
      possiblePriceDeviation: [data.possiblePriceDeviation],
      submissionsWithdrawableByOwner: [data.submissionsWithdrawableByOwner],
      ticketPersonalizationEnabled: [false],
      showPrices: [data.showPrices],
      orderRequestRequired: [data.orderRequestRequired],
      specialRequirement: data.specialRequirement,
      delivery: this.formBuilder.group({
        deliveryMethod: [data.delivery.deliveryMethod, [Validators.required]],
        deliveryFee: [data.delivery.deliveryFee.value || 400, [Validators.required]],
      }),
      submissionCollectionTiming: this.formBuilder.group({
        start: [timing.start?.dateIso, [Validators.required]],
        deadline: [timing.deadline?.dateIso, [Validators.required]],
      }, {validators: ValidateDateRange('start', 'deadline')})
    });
  }

  /**
   * Gets the correct key and translated it
   */
  getTranslatedKey(key: string): string {
    let translatedKey = '';
    switch (key) {
      case DeliveryMethodsType.COLLECTION:
        translatedKey = this.flTranslations.i18nCollection;
        break;
      case DeliveryMethodsType.MAIL_ORDER:
        translatedKey = this.flTranslations.i18nMailOrder;
        break;
      case DeliveryMethodsType.MOBILE_TICKET:
        translatedKey = this.flTranslations.i18nMobileTicket;
        break;
      case DeliveryMethodsType.PRINT_AT_HOME:
        translatedKey = this.flTranslations.i18nPrintAtHome;
        break;
      default:
    }
    return translatedKey;
  }

  /**
   * If form is valid update the process configuration, otherwise display the errors.
   */
  public updateFormSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      const formValue = form.value;

      this.spinnerService.show(this.spinnerName);
      let newAmount = (formValue.delivery.deliveryFee * 100);
      newAmount = Math.trunc(newAmount);
      formValue.delivery.deliveryFee = newAmount.toString();
      formValue.submissionCollectionTiming.start = new Date(formValue.submissionCollectionTiming.start);
      formValue.submissionCollectionTiming.deadline = new Date(formValue.submissionCollectionTiming.deadline);

      this.processConfigurationService.update(formValue).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.processConfigurationService.setCurrentProcessConfiguration(data);
            this.spinnerService.hide(this.spinnerName);
            this.tixxSelectOverviewService.toggleEditProcessConfigurationModal(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
            });
            this.fillForm();
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.tixxSelectTranslationService.i18nErrorUpdateLottery
            });
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

}
